import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContactSummary,
  getContacts,
  replyToContact,
  updateContactMessage,
} from '../services/adminApi';

interface ContactFormState {
  replyText: string;
  assignedTo: string;
  isResolved: boolean;
  saving: boolean;
  replying: boolean;
  error: string | null;
  success: string | null;
}

const ContactsPage = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const [formState, setFormState] = useState<Record<string, ContactFormState>>({});
  const [error, setError] = useState<string | null>(null);

  const loadContacts = async () => {
    setError(null);
    try {
      const items = await getContacts();
      setContacts(items);
      setFormState((current) => {
        const next = { ...current };
        items.forEach((contact) => {
          next[contact.id] = {
            replyText: current[contact.id]?.replyText ?? '',
            assignedTo: contact.assignedTo ?? current[contact.id]?.assignedTo ?? '',
            isResolved: contact.isResolved,
            saving: false,
            replying: false,
            error: null,
            success: null,
          };
        });
        return next;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.contactsLoadFailed', 'Failed to load contact leads.'));
    }
  };

  useEffect(() => {
    void loadContacts();
  }, []);

  const updateStateForContact = (id: string, patch: Partial<ContactFormState>) => {
    setFormState((current) => ({
      ...current,
      [id]: {
        ...current[id],
        ...patch,
      },
    }));
  };

  const handleSaveContact = async (contact: ContactSummary) => {
    const state = formState[contact.id];
    if (!state) return;

    updateStateForContact(contact.id, { saving: true, error: null, success: null });
    try {
      await updateContactMessage(contact.id, {
        isResolved: state.isResolved,
        assignedTo: state.assignedTo.trim() || null,
      });
      await loadContacts();
      updateStateForContact(contact.id, {
        saving: false,
        success: t('contacts.saveSuccess', 'Contact updated successfully.'),
      });
    } catch (err: unknown) {
      updateStateForContact(contact.id, {
        saving: false,
        error: err instanceof Error ? err.message : t('contacts.updateFailed', 'Failed to update contact lead.'),
      });
    }
  };

  const handleReply = async (contact: ContactSummary) => {
    const state = formState[contact.id];
    if (!state) return;

    updateStateForContact(contact.id, { replying: true, error: null, success: null });
    try {
      await replyToContact(contact.id, { replyBody: state.replyText });
      updateStateForContact(contact.id, {
        replying: false,
        replyText: '',
        success: t('contacts.replySuccess', 'Reply sent successfully.'),
      });
    } catch (err: unknown) {
      updateStateForContact(contact.id, {
        replying: false,
        error: err instanceof Error ? err.message : t('contacts.replyFailed', 'Failed to send reply.'),
      });
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{t('pages.contactsTitle')}</h2>
        <p>{t('pages.contactsDescription')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="overview-grid">
        {contacts.map((contact) => {
          const state = formState[contact.id] ?? {
            replyText: '',
            assignedTo: contact.assignedTo ?? '',
            isResolved: contact.isResolved,
            saving: false,
            replying: false,
            error: null,
            success: null,
          };

          return (
            <article key={contact.id} className="contact-card">
              <div className="contact-card-header">
                <div>
                  <h3>{contact.fullName}</h3>
                  <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                    {contact.email} · {contact.phone}
                  </p>
                </div>
                <span className={`status-badge ${contact.isResolved ? 'resolved' : 'pending'}`}>
                  {contact.isResolved ? t('pages.contactsResolved') : t('pages.contactsPending')}
                </span>
              </div>

              <div className="contact-card-meta">
                <p style={{ margin: 0, fontWeight: 700 }}>{contact.subject}</p>
                <p style={{ margin: '0.75rem 0' }}>{contact.message}</p>
                <p className="muted" style={{ margin: 0 }}>
                  {t('contacts.sourceLabel', 'Source')}: {contact.sourcePage}
                </p>
                <p className="muted" style={{ margin: 0 }}>
                  {t('contacts.receivedAtLabel', 'Received')}: {new Date(contact.createdAt).toLocaleString()}
                </p>
              </div>

              {state.error && <div className="alert alert-error">{state.error}</div>}
              {state.success && <div className="alert alert-success">{state.success}</div>}

              <div className="form-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={state.isResolved}
                      onChange={(e) => updateStateForContact(contact.id, { isResolved: e.target.checked })}
                    />{' '}
                    {t('pages.contactsResolved')}
                  </label>
                </div>

                <div className="form-group">
                  <label>{t('contacts.assignTo')}</label>
                  <input
                    className="form-control"
                    value={state.assignedTo}
                    onChange={(e) => updateStateForContact(contact.id, { assignedTo: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions form-actions-row">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={state.saving}
                  onClick={() => void handleSaveContact(contact)}
                >
                  {state.saving ? t('common.loading') : t('contacts.saveContact')}
                </button>
              </div>

              <section className="reply-panel">
                <div className="form-group">
                  <label>{t('contacts.replyLabel')}</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={state.replyText}
                    onChange={(e) => updateStateForContact(contact.id, { replyText: e.target.value })}
                    placeholder={t('contacts.replyPlaceholder')}
                  />
                </div>
                <div className="form-actions form-actions-row">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={state.replying || !state.replyText.trim()}
                    onClick={() => void handleReply(contact)}
                  >
                    {state.replying ? t('common.loading') : t('contacts.sendReply')}
                  </button>
                </div>
              </section>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default ContactsPage;
